import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { Popover } from 'antd'
import { QuestionCircleOutlined } from '@ant-design/icons'
import { Text } from '@lmc/ui-kit/src/general'
import { NAMESPACE } from 'locale'
import { AdministrationStatus } from '../types'
import { getAdministrationStatusDisplay } from '../utils/administrationUtils'

const ADMINISTRATION_STATUSES_FOR_LEGEND: AdministrationStatus[] = [
  AdministrationStatus.GIVEN,
  AdministrationStatus.VERBAL_PROMPT,
  AdministrationStatus.ABSENT,
  AdministrationStatus.OTHER,
  AdministrationStatus.ALREADY_TAKEN,
  // AdministrationStatus.NOT_NEEDED,
  AdministrationStatus.LEFT_OUT,
  // AdministrationStatus.PAUSED,
  AdministrationStatus.NAUSEA_VOMITING,
  AdministrationStatus.DISCONTINUED,
  AdministrationStatus.REFUSED,
  // AdministrationStatus.MISSED,
  AdministrationStatus.REFUSED_DESTROYED
]

interface LegendProps {
  testId?: string
}

const Legend = ({ testId }: LegendProps = {}) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const theme = useTheme()

  const legendItems = ADMINISTRATION_STATUSES_FOR_LEGEND.map((status) => ({
    status,
    ...getAdministrationStatusDisplay(status)
  }))

  return (
    <Popover
      placement="bottomRight"
      overlayInnerStyle={{
        borderRadius: '9px',
        padding: '10px 16px'
      }}
      content={
        <LegendContent
          data-testid={testId ? `${testId}-emar-legend-content` : 'emar-legend-content'}
        >
          {legendItems.map((item) => (
            <LegendItemContainer
              key={item.translationKey}
              data-testid={
                testId
                  ? `${testId}-emar-legend-item-${item.translationKey}`
                  : `emar-legend-item-${item.translationKey}`
              }
            >
              <LegendCodeContainer
                data-testid={
                  testId
                    ? `${testId}-emar-legend-code-${item.translationKey}`
                    : `emar-legend-code-${item.translationKey}`
                }
              >
                {item.display}
              </LegendCodeContainer>
              <Text
                data-testid={
                  testId
                    ? `${testId}-emar-legend-text-${item.translationKey}`
                    : `emar-legend-text-${item.translationKey}`
                }
              >
                {t(item.translationKey)}
              </Text>
            </LegendItemContainer>
          ))}
        </LegendContent>
      }
    >
      <LegendContainer
        data-testid={testId ? `${testId}-emar-legend-button` : 'emar-legend-button'}
      >
        <QuestionCircleOutlined
          style={{ fontSize: 16, color: theme.color.neutral.color4 }}
          data-testid={testId ? `${testId}-emar-legend-icon` : 'emar-legend-icon'}
        />
        <StyledText
          level={2}
          data-testid={testId ? `${testId}-emar-legend-label` : 'emar-legend-label'}
        >
          {t('LEGEND')}
        </StyledText>
      </LegendContainer>
    </Popover>
  )
}

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
`

const LegendContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 24px;
  min-width: 300px;
`

const LegendItemContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const LegendCodeContainer = styled.div`
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const LegendCode = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

export default Legend
